import {
  createErrorResult,
  createErrorResultFromException,
  createSuccessResult,
  errors,
  RydeUiUnit,
  RydeUiUnitDropdownCacheKey,
  SimpleResult,
  ViewApi,
  ViewDto,
} from "@ryde/shared";

interface IAppendSuffixToEntityParams {
  entity: RydeUiUnit;
  suffix?: string;
}

export const appendSuffixToEntity = ({
  entity,
  suffix,
}: IAppendSuffixToEntityParams): RydeUiUnitDropdownCacheKey => {
  let result: RydeUiUnitDropdownCacheKey = entity;

  if (suffix) {
    result += `_${suffix}`;
  }

  return result as RydeUiUnitDropdownCacheKey;
};

export const fetchViews = async ({
  entityId,
  companyId,
}: {
  entityId: number | undefined;
  companyId?: number;
}): Promise<SimpleResult<ViewDto[]>> => {
  if (!companyId) {
    return createErrorResult(errors.common.invalidCompanyId);
  }

  if (!entityId) {
    return createErrorResult(errors.common.invalidEntity);
  }

  try {
    const viewDtos = await ViewApi.getAllForEntity({ entityId, companyId });
    return createSuccessResult(viewDtos ?? []);
  } catch (error) {
    return createErrorResultFromException(error, "Failed to fetch views");
  }
};

export const getIsViewsFetchReady = ({
  companyId,
  entityId,
}: {
  companyId: number | undefined;
  entityId: number | undefined;
}): boolean => Boolean(companyId && entityId);

/**
 * Resolves the value the views atom must hold after a fetch, successful or not.
 *
 * `undefined` in `viewDtosAtomFamily` is the "views are still loading" sentinel behind `isViewLoadedSF`, and
 * that flag gates the grid loader (`use-table-wrapper`) as well as the whole final schema init chain
 * (`use-final-schema-service`). A failed request never resolves later, so the sentinel has to be cleared or
 * every loader waiting on it spins forever. Two failures are not the same, though:
 *
 * - the request ran and failed: terminal, so clear the sentinel — to an empty list when nothing was loaded
 *   yet, otherwise to the list already on screen, which a failed refresh must not wipe;
 * - `getIsViewsFetchReady` is false: no request ran, the IDs are still resolving and init runs again once
 *   they land, so the sentinel stays in place instead of marking empty views as loaded.
 */
export const resolveViewDtosToSet = ({
  viewsResult,
  previousViewDtos,
  isViewsFetchReady,
}: {
  viewsResult: SimpleResult<ViewDto[]>;
  previousViewDtos: ViewDto[] | undefined;
  isViewsFetchReady: boolean;
}): ViewDto[] | undefined => {
  if (viewsResult.success) {
    return viewsResult.data ?? [];
  }

  if (!isViewsFetchReady) {
    return previousViewDtos;
  }

  return previousViewDtos ?? [];
};
